import type { MutableRefObject } from 'react';
import {
	createStore,
	createHook,
	createActionsHook,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
	type Store as SweetStateStore,
} from 'react-sweet-state';

type CreateSpaceButtonLocationType =
	| 'topCreateNav'
	| 'personalizedTemplatesV2ExperimentSetupTeamSpaceCard'
	| undefined;

type State = {
	showCreateSpaceDrawer: boolean;
	shouldRenderDrawer: boolean;
	createSpaceButtonRef: MutableRefObject<HTMLElement | null>;
	createSpaceButtonLocation: CreateSpaceButtonLocationType;
	/**
	 * When true, `ConditionalCreateSpaceDrawer` renders the standard drawer even
	 * if the smart-create experiment is on. Used as a fallback when AI space
	 * creation can't produce a suggestion, so the user can still set up a space
	 * manually. Reset on every open/close so a fresh drawer defaults back to AI.
	 */
	fallbackToStandard: boolean;
};

const initialState: State = {
	showCreateSpaceDrawer: false,
	shouldRenderDrawer: false,
	createSpaceButtonRef: {
		current: null,
	} as MutableRefObject<HTMLElement | null>,
	createSpaceButtonLocation: undefined,
	fallbackToStandard: false,
};
export const actions = {
	openDrawer:
		(
			value: MutableRefObject<HTMLElement | null>,
			createSpaceButtonLocation: CreateSpaceButtonLocationType,
		) =>
		({ setState }: StoreActionApi<State>): void => {
			setState({
				showCreateSpaceDrawer: true,
				shouldRenderDrawer: true,
				createSpaceButtonRef: value,
				createSpaceButtonLocation,
				fallbackToStandard: false,
			});
		},
	closeDrawer:
		() =>
		({ setState }: StoreActionApi<State>): void => {
			setState({
				showCreateSpaceDrawer: false,
				createSpaceButtonLocation: undefined,
				fallbackToStandard: false,
			});
		},
	/**
	 * Switches an open smart-create drawer over to the standard create flow
	 * without closing it, so the user keeps their place in the drawer. The drawer
	 * stays open (`showCreateSpaceDrawer` is untouched); only which drawer
	 * `ConditionalCreateSpaceDrawer` renders changes.
	 */
	switchToStandardCreate:
		() =>
		({ setState }: StoreActionApi<State>): void => {
			setState({ fallbackToStandard: true });
		},
};

type Actions = {
	openDrawer: (
		value: MutableRefObject<HTMLElement | null>,
		createSpaceButtonLocation: CreateSpaceButtonLocationType,
	) => ({ setState }: StoreActionApi<State>) => void;
	closeDrawer: () => ({ setState }: StoreActionApi<State>) => void;
	switchToStandardCreate: () => ({ setState }: StoreActionApi<State>) => void;
};

export const Store: SweetStateStore<State, Actions> = createStore({
	initialState,
	actions,
	name: 'createSpaceStore',
});

export const useCreateSpaceStore: HookFunction<
	State,
	BoundActions<State, Actions>,
	void
> = createHook(Store);

const getShouldRenderDrawer = (state: { shouldRenderDrawer: boolean }) => {
	return state.shouldRenderDrawer;
};

export const useShouldRenderDrawer: HookFunction<
	boolean,
	BoundActions<State, Actions>,
	void
> = createHook(Store, {
	selector: getShouldRenderDrawer,
});

const getIsCreateSpaceDrawerShowing = (state: { showCreateSpaceDrawer: boolean }) => {
	return state.showCreateSpaceDrawer;
};

export const useIsCreateSpaceDrawerShowing: HookFunction<
	boolean,
	BoundActions<State, Actions>,
	void
> = createHook(Store, {
	selector: getIsCreateSpaceDrawerShowing,
});
export const useCreateSpaceStoreActions: HookActionsFunction<BoundActions<State, Actions>> =
	createActionsHook(Store);
